import { Link, useLocation } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FileText,
  Users,
  AlertTriangle,
  DollarSign,
  Activity,
  BarChart3,
  Settings,
  Zap,
  Shield,
  Lock,
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, minRole: 'viewer' },
  { name: 'Invoices', href: '/invoices', icon: FileText, minRole: 'viewer' },
  { name: 'Vendors', href: '/vendors', icon: Users, minRole: 'viewer' },
  { name: 'Findings', href: '/findings', icon: AlertTriangle, minRole: 'viewer' },
  { name: 'Recoveries', href: '/recoveries', icon: DollarSign, minRole: 'analyst' },
  { name: 'Health', href: '/health', icon: Activity, minRole: 'viewer' },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, minRole: 'viewer', minTier: 'pro' },
  { name: 'Integrations', href: '/integrations', icon: Zap, minRole: 'admin' },
  { name: 'Settings', href: '/settings', icon: Settings, minRole: 'viewer' },
];

const roleHierarchy = {
  viewer: 1,
  analyst: 2,
  admin: 3,
  owner: 4,
};

const tierHierarchy = {
  trial: 1,
  starter: 2,
  pro: 3,
  enterprise: 4,
};

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isOpen = false, onClose }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();

  const userRole = user?.role || 'viewer';
  const userTier = user?.tier || 'trial';
  const userRoleLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 1;
  const userTierLevel = tierHierarchy[userTier as keyof typeof tierHierarchy] || 1;

  const canAccessItem = (item: typeof navigation[0]) => {
    const requiredRoleLevel = roleHierarchy[item.minRole as keyof typeof roleHierarchy] || 1;
    const hasRole = userRoleLevel >= requiredRoleLevel;

    if (item.minTier) {
      const requiredTierLevel = tierHierarchy[item.minTier as keyof typeof tierHierarchy] || 1;
      return hasRole && userTierLevel >= requiredTierLevel;
    }

    return hasRole;
  };

  return (
    <div
      className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-slate-950/95 backdrop-blur-xl border-r border-slate-800/50 flex flex-col transition-transform duration-200 ease-in-out lg:static lg:z-auto lg:translate-x-0 lg:bg-slate-950/80 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="p-6 border-b border-slate-800/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/20">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Vigilant</h1>
            <p className="text-xs text-slate-400">Revenue Protector</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          const canAccess = canAccessItem(item);

          if (!canAccess) {
            return (
              <div
                key={item.name}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 cursor-not-allowed relative group"
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
                <Lock className="w-3 h-3 ml-auto" />
                <div className="absolute left-full ml-2 px-2 py-1 bg-slate-800 text-xs text-slate-300 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {item.minTier && userTierLevel < tierHierarchy[item.minTier as keyof typeof tierHierarchy]
                    ? `Requires ${item.minTier} plan`
                    : `Requires ${item.minRole} role`}
                </div>
              </div>
            );
          }

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 shadow-lg shadow-cyan-500/10'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }
              `}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800/50">
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-lg p-4 border border-slate-700/50">
          <p className="text-xs text-slate-400 mb-1">Protected</p>
          <p className="text-2xl font-bold text-white">$0.00</p>
          <p className="text-xs text-slate-500 mt-1">Total savings</p>
        </div>
      </div>
    </div>
  );
}
